"use client";
/**
 * Modal Component
 *
 * A reusable modal dialog for displaying content in an overlay.
 * - Uses React Portal to render above all content
 * - Supports custom title, close button, z-index, and width
 * - Handles click outside to close
 * - Accepts children for modal content
 */
import { X } from "lucide-react";
import type React from "react";
import { useRef } from "react";
import { createPortal } from "react-dom";

export interface ModalProps {
  isOpen: boolean;
  title?: React.ReactNode;
  onClose: () => void;
  children?: React.ReactNode;
  zIndex?: number;
  showClose?: boolean;
  width?: string;
  height?: string;
  backdropClassName?: string;
  backdropStyle?: React.CSSProperties;
  innerClassName?: string;
  innerStyle?: React.CSSProperties;
}

const CModal: React.FC<ModalProps> = ({
  isOpen,
  title,
  onClose,
  children,
  zIndex = 999999,
  showClose = true,
  width = "max-w-lg",
  height = "max-h-[90vh]",
  backdropClassName,
  backdropStyle,
  innerClassName,
  innerStyle,
}) => {
  const mouseDownOnBackdrop = useRef(false);

  if (!isOpen) return null;
  if (typeof document === "undefined") return null;

  return createPortal(
    // biome-ignore lint/a11y/noStaticElementInteractions: backdrop click is a UX convention, not a primary interaction
    <div
      className={`fixed inset-0 flex items-center justify-center p-4 bg-black/10 pointer-events-auto ${backdropClassName || ""}`}
      style={{ zIndex, ...(backdropStyle || {}) }}
      onMouseDown={(e) => {
        mouseDownOnBackdrop.current = e.target === e.currentTarget;
      }}
      onMouseUp={(e) => {
        if (mouseDownOnBackdrop.current && e.target === e.currentTarget) {
          onClose();
        }
        mouseDownOnBackdrop.current = false;
      }}
    >
      {/* biome-ignore lint/a11y/noStaticElementInteractions: stopPropagation only — no real interaction */}
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: stopPropagation only — no real interaction */}
      <div
        className={`bg-white dark:bg-black rounded-lg shadow-2xl w-full ${width} relative border ${height} border-gray-200 dark:border-gray-700 flex flex-col ${innerClassName || ""}`}
        style={{ zIndex: zIndex + 1, ...(innerStyle || {}) }}
        onClick={(e) => e.stopPropagation()}
      >
        {(title || showClose) && (
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            {title &&
              (typeof title === "string" ? (
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {title}
                </h3>
              ) : (
                <div className="text-lg font-semibold text-gray-900 dark:text-white">
                  {title}
                </div>
              ))}
            {showClose && (
              <button
                type="button"
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
              >
                <X size={20} />
              </button>
            )}
          </div>
        )}
        <div className="p-4 overflow-y-auto flex-grow">{children}</div>
      </div>
    </div>,
    document.body,
  );
};

export default CModal;
